import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ZarityMark } from "@/components/icons/ZarityMark";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuthStore } from "@/stores/authStore";
import { sendOtp, verifyOtp, resetRefreshState } from "@/lib/api";
import type { ApiError, OtpChannel } from "@/types/api";

const RESEND_COOLDOWN_SECONDS = 15;

// Says where to look for the code. Without a reported channel, Slack is the
// default route for staff, so point there first.
const describeOtpDelivery = (
  channel: OtpChannel | undefined,
  phone: string
) => {
  switch (channel) {
    case "slack":
      return "Check your Slack DMs from Zarity Login.";
    case "sms":
      return `We sent it by SMS to ${phone}.`;
    case "whatsapp":
      return `We sent it on WhatsApp to ${phone}.`;
    default:
      return `Check your Slack DMs from Zarity Login, or SMS on ${phone}.`;
  }
};

export const PhoneLogin = () => {
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [otp, setOtp] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [otpInfoMessage, setOtpInfoMessage] = useState<string | null>(null);
  const [otpChannel, setOtpChannel] = useState<OtpChannel | undefined>();
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const { login } = useAuthStore();
  const isPhoneValid = phoneDigits.length === 10;
  const apiPhoneNumber = `+91${phoneDigits}`;
  const shouldShowPhoneValidation =
    phoneTouched && phoneDigits.length > 0 && !isPhoneValid;
  const phoneValidationMessage = "Enter a 10-digit phone number.";

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneDigits(digitsOnly);
    setPhoneTouched(true);
    setSendError(null);
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    const apiError = error as Partial<ApiError>;
    const detailString =
      typeof apiError.details === "string" ? apiError.details : undefined;
    if (
      apiError.message === "Restricted access" ||
      detailString === "User not found"
    ) {
      return "This phone number isn't registered with Zarity. Please contact the Zarity team if you should have access.";
    }
    return apiError.message || fallback;
  };

  const startResendCooldown = (seconds = RESEND_COOLDOWN_SECONDS) => {
    setResendCooldownSeconds(Math.max(1, Math.ceil(seconds)));
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp("");
    setSendError(null);
    setVerifyError(null);
    setOtpInfoMessage(null);
    setResendCooldownSeconds(0);
  };

  const handleSendOTP = async () => {
    setPhoneTouched(true);
    setSendError(null);

    if (!isPhoneValid) {
      setSendError(phoneValidationMessage);
      return;
    }

    setIsSending(true);
    try {
      const response = await sendOtp(apiPhoneNumber);
      setOtpChannel(response.otp_channel);
      setStep("otp");
      setOtp("");
      setVerifyError(null);
      setOtpInfoMessage(null);
      startResendCooldown();
    } catch (error) {
      setSendError(
        getErrorMessage(error, "Failed to send OTP. Please try again.")
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    setVerifyError(null);
    setIsVerifying(true);
    try {
      const response = await verifyOtp(apiPhoneNumber, otp);
      // Check if OTP verification was successful
      if (
        response.otp_verify &&
        response.user &&
        response.token &&
        response.refresh_token
      ) {
        const { user, token, refresh_token } = response;
        const userWithPermissions = {
          ...user,
          permissions: user.permissions ?? response.permissions ?? [],
        };

        // Reset refresh state for new login session
        resetRefreshState();

        login(userWithPermissions, token, refresh_token);
      } else if (!response.otp_verify) {
        throw new Error("Invalid OTP. Please check your code and try again.");
      } else {
        throw new Error("Invalid response format or missing user data.");
      }
    } catch (error) {
      setVerifyError(
        getErrorMessage(error, "Please check your code and try again.")
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldownSeconds > 0 || isResending) {
      return;
    }

    setVerifyError(null);
    setOtpInfoMessage(null);
    setIsResending(true);
    try {
      const response = await sendOtp(apiPhoneNumber);
      setOtpChannel(response.otp_channel);
      startResendCooldown();
      setOtpInfoMessage("New code sent. The earlier one no longer works.");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.retry_after_seconds) {
        startResendCooldown(apiError.retry_after_seconds);
      }
      setVerifyError(
        apiError.message || "Failed to resend OTP. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldownSeconds((currentSeconds) =>
        Math.max(0, currentSeconds - 1)
      );
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldownSeconds]);

  return (
    <div className="min-h-screen flex flex-col gap-6 items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-3xl overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2 md:min-h-[480px]">
          <form
            className="p-6 md:p-8 h-full"
            onSubmit={(e) => {
              e.preventDefault();
              if (step === "phone") {
                if (isPhoneValid && !isSending) handleSendOTP();
              } else if (otp.length === 6 && !isVerifying) {
                handleVerifyOTP();
              }
            }}
          >
            {step === "phone" ? (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">Welcome back</h1>
                  <p className="text-muted-foreground text-balance">
                    Login to your Zarity account
                  </p>
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="98765 43210"
                    value={phoneDigits}
                    onChange={handlePhoneChange}
                    onBlur={() => setPhoneTouched(true)}
                    inputMode="numeric"
                    maxLength={10}
                    title={phoneValidationMessage}
                    required
                  />
                  {shouldShowPhoneValidation && (
                    <p className="text-sm text-destructive">
                      {phoneValidationMessage}
                    </p>
                  )}
                  {sendError && (
                    <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertDescription>{sendError}</AlertDescription>
                    </Alert>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={!isPhoneValid || isSending}
                >
                  {isSending ? "Sending..." : "Continue"}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">Verify OTP</h1>
                  <p className="text-muted-foreground text-balance">
                    {describeOtpDelivery(otpChannel, apiPhoneNumber)}
                  </p>
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="otp">Enter OTP</Label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(value) => setOtp(value)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {verifyError ? (
                    <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertDescription>{verifyError}</AlertDescription>
                    </Alert>
                  ) : otpInfoMessage ? (
                    <Alert>
                      <AlertDescription>{otpInfoMessage}</AlertDescription>
                    </Alert>
                  ) : null}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={otp.length !== 6 || isVerifying}
                >
                  {isVerifying ? "Verifying..." : "Verify"}
                </Button>
                <div className="flex flex-col items-center gap-1 text-center">
                  <div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResendOTP}
                      disabled={resendCooldownSeconds > 0 || isResending}
                    >
                      {resendCooldownSeconds > 0
                        ? `Resend in ${resendCooldownSeconds}s`
                        : isResending
                          ? "Resending..."
                          : "Resend OTP"}
                    </Button>
                    <span className="mx-2 text-muted-foreground">|</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleChangeNumber}
                    >
                      Change number
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form>
          {/* Brand panel — sidebar tokens, not primary: the reversed mark is
              defined against them, and they stay dark in both themes (primary
              flips to citron in dark and swallows the mark's citron ring). */}
          <div className="bg-sidebar hidden md:flex flex-col items-center justify-center gap-4 min-h-[280px] md:min-h-[480px]">
            <ZarityMark variant="reversed" size={88} />
            <span className="font-heading text-sidebar-foreground text-4xl font-bold tracking-[-0.03em]">
              zarity
            </span>
          </div>
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
        and <a href="#">Privacy Policy</a>.
      </div>
    </div>
  );
};
